import { SessionData, AnalysisResult } from '../types';
import { getTrainingRecommendations } from './classify';

export function generatePDFReport(sessionData: SessionData, analysisResult: AnalysisResult): void {
  const { athlete, session } = sessionData;
  const { fvProfile, classification, zones, quality, splits } = analysisResult;
  const recommendations = getTrainingRecommendations(classification);

  // Create HTML content for PDF
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        body { 
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
          line-height: 1.6; 
          color: #1A1B23;
          background: #ffffff;
          min-height: 100vh;
          padding: 20px;
        }
        
        .container {
          max-width: 700px;
          margin: 0 auto;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          border: 1px solid #E5E7EB;
        }
        
        .header { 
          background: #1A1B23;
          color: white;
          padding: 30px;
          text-align: center;
          position: relative;
          border-bottom: 4px solid #E11D48;
        }
        
        .header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
        }
        
        .header h1 { 
          font-size: 28px; 
          font-weight: 700; 
          margin-bottom: 8px;
          position: relative;
          z-index: 1;
        }
        
        .header .subtitle { 
          font-size: 16px; 
          opacity: 0.9;
          position: relative;
          z-index: 1;
        }
        
        .content { padding: 30px; }
        
        .section { 
          margin-bottom: 32px;
          background: #F9FAFB;
          border-radius: 12px;
          padding: 24px;
          border: 1px solid #E5E7EB;
        }
        
        .section h2 { 
          font-size: 20px; 
          font-weight: 600; 
          margin-bottom: 16px;
          color: #1A1B23;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .section h2::before {
          content: '';
          width: 4px;
          height: 20px;
          background: #E11D48;
          border-radius: 2px;
        }
        
        .athlete-info {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-top: 16px;
        }
        
        .info-item {
          background: white;
          padding: 12px 16px;
          border-radius: 8px;
          border-left: 4px solid #6B7280;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
        
        .info-label {
          font-size: 12px;
          font-weight: 500;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .info-value {
          font-size: 16px;
          font-weight: 600;
          color: #1A1B23;
          margin-top: 2px;
        }
        
        .classification-section {
          text-align: center;
          background: #F9FAFB;
        }
        
        .badge { 
          display: inline-block;
          padding: 12px 24px; 
          border-radius: 50px; 
          color: white; 
          font-weight: 600;
          font-size: 18px;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          margin: 8px 0;
        }
        
        .speed { background: #0EA5E9; }
        .force { background: #E11D48; }
        .balanced { background: #10B981; }
        
        .kpi-grid { 
          display: grid; 
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); 
          gap: 16px; 
          margin: 20px 0; 
        }
        
        .kpi-card { 
          background: white;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          transition: transform 0.2s;
          position: relative;
          overflow: hidden;
        }
        
        .kpi-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: #E5E7EB;
        }
        
        .kpi-label {
          font-size: 14px;
          font-weight: 500;
          color: #6B7280;
          margin-bottom: 8px;
        }
        
        .kpi-value {
          font-size: 24px;
          font-weight: 700;
          color: #1A1B23;
          margin-bottom: 4px;
        }
        
        .kpi-unit {
          font-size: 12px;
          color: #6B7280;
        }
        
        .zone { 
          padding: 16px; 
          margin: 12px 0; 
          border-radius: 10px;
          border-left: 6px solid;
          background: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .zone-technique { border-left-color: #10B981; background: #F0FDF4; }
        .zone-speed-strength { border-left-color: #0EA5E9; background: #F0F9FF; }
        .zone-power { border-left-color: #8B5CF6; background: #FAF5FF; }
        .zone-strength-speed { border-left-color: #6B7280; background: #F9FAFB; }
        
        .zone-info {
          flex: 1;
        }
        
        .zone-name {
          font-weight: 600;
          font-size: 16px;
          color: #1A1B23;
          margin-bottom: 4px;
        }
        
        .zone-desc {
          font-size: 14px;
          color: #6B7280;
        }
        
        .zone-range {
          font-weight: 600;
          font-size: 16px;
          color: #1A1B23;
          background: white;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid #E5E7EB;
        }
        
        .recommendations {
          background: #FFFBEB;
          border-left: 4px solid #F59E0B;
        }
        
        .recommendations ul {
          list-style: none;
          margin: 16px 0;
        }
        
        .recommendations li {
          background: white;
          margin: 8px 0;
          padding: 12px 16px;
          border-radius: 8px;
          border-left: 4px solid #F59E0B;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          position: relative;
        }
        
        .recommendations li::before {
          content: '💡';
          margin-right: 8px;
        }
        
        .quality-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin: 16px 0;
        }
        
        .quality-item {
          background: white;
          padding: 12px 16px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .quality-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
        }
        
        .pass .quality-icon { background: #10B981; color: white; }
        .fail .quality-icon { background: #EF4444; color: white; }
        
        .quality-text {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
        }
        
        .pass .quality-text { color: #065F46; }
        .fail .quality-text { color: #991B1B; }
        
        .footer {
          text-align: center;
          padding: 20px;
          background: #F9FAFB;
          border-top: 1px solid #E5E7EB;
          font-size: 12px;
          color: #6B7280;
        }
        
        @page {
          size: A4 portrait;
          margin: 0.5in;
        }
        
        @media print {
          body { background: white; padding: 0; }
          .container { box-shadow: none; max-width: none; }
          .kpi-grid { grid-template-columns: repeat(2, 1fr); }
          .athlete-info { grid-template-columns: repeat(2, 1fr); }
          .quality-grid { grid-template-columns: 1fr; }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>T-APEX PERFORMANCE ANALYSIS</h1>
          <p class="subtitle">Advanced Sprint Force-Velocity Profiling Report</p>
          <p class="subtitle">Generated on ${session.date} for ${athlete.name}</p>
          <p class="subtitle">by Gamefit Training</p>
        </div>
        
        <div class="content">
          <div class="section">
            <h2>👤 Athlete Information</h2>
            <div class="athlete-info">
              <div class="info-item">
                <div class="info-label">Athlete Name</div>
                <div class="info-value">${athlete.name}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Body Mass</div>
                <div class="info-value">${athlete.massKg} kg</div>
              </div>
              <div class="info-item">
                <div class="info-label">Sport</div>
                <div class="info-value">${athlete.sport}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Position</div>
                <div class="info-value">${athlete.position}</div>
              </div>
            </div>
          </div>
          
          <div class="section classification-section">
            <h2>🎯 Athlete Classification</h2>
            <div class="badge ${classification.label === 'Speed-oriented' ? 'speed' : classification.label === 'Force-oriented' ? 'force' : 'balanced'}">
              ${classification.label.toUpperCase()}
            </div>
            <p style="margin-top: 12px; color: #64748b; font-size: 14px;">Δ = ${classification.delta.toFixed(3)} | zF = ${classification.zF.toFixed(3)} | zV = ${classification.zV.toFixed(3)}</p>
          </div>
          
          <div class="section">
            <h2>📊 Key Performance Indicators</h2>
            <div class="kpi-grid">
              <div class="kpi-card">
                <div class="kpi-label">Maximum Force</div>
                <div class="kpi-value">${fvProfile.F0_rel.toFixed(2)}</div>
                <div class="kpi-unit">N/kg</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-label">Maximum Velocity</div>
                <div class="kpi-value">${fvProfile.V0.toFixed(2)}</div>
                <div class="kpi-unit">m/s</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-label">Peak Power</div>
                <div class="kpi-value">${fvProfile.Pmax_rel.toFixed(1)}</div>
                <div class="kpi-unit">W/kg</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-label">Model Fit</div>
                <div class="kpi-value">${fvProfile.R2.toFixed(3)}</div>
                <div class="kpi-unit">R²</div>
              </div>
            </div>
            ${splits ? `
            <div class="kpi-grid" style="grid-template-columns: repeat(2, 1fr); margin-top: 16px;">
              <div class="kpi-card">
                <div class="kpi-label">Max Sprint Speed</div>
                <div class="kpi-value">${splits.MSS.toFixed(2)}</div>
                <div class="kpi-unit">m/s</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-label">Max Acceleration</div>
                <div class="kpi-value">${splits.MAC.toFixed(2)}</div>
                <div class="kpi-unit">m/s²</div>
              </div>
            </div>` : ''}
          </div>
          
          <div class="section">
            <h2>🎯 Training Zones</h2>
            <div class="zone zone-technique">
              <div class="zone-info">
                <div class="zone-name">Technique Zone</div>
                <div class="zone-desc">Technical development • 0-10% velocity loss</div>
              </div>
              <div class="zone-range">${zones.Technique[0].toFixed(1)} - ${zones.Technique[1].toFixed(1)} kgf</div>
            </div>
            <div class="zone zone-speed-strength">
              <div class="zone-info">
                <div class="zone-name">Speed-Strength Zone</div>
                <div class="zone-desc">Speed-strength development • 10-25% velocity loss</div>
              </div>
              <div class="zone-range">${zones.SpeedStrength[0].toFixed(1)} - ${zones.SpeedStrength[1].toFixed(1)} kgf</div>
            </div>
            <div class="zone zone-power">
              <div class="zone-info">
                <div class="zone-name">Power Zone</div>
                <div class="zone-desc">Power development • 25-40% velocity loss</div>
              </div>
              <div class="zone-range">${zones.Power[0].toFixed(1)} - ${zones.Power[1].toFixed(1)} kgf</div>
            </div>
            <div class="zone zone-strength-speed">
              <div class="zone-info">
                <div class="zone-name">Strength-Speed Zone</div>
                <div class="zone-desc">Strength-speed development • 40-55% velocity loss</div>
              </div>
              <div class="zone-range">${zones.StrengthSpeed[0].toFixed(1)} - ${zones.StrengthSpeed[1].toFixed(1)} kgf</div>
            </div>
          </div>
          
          <div class="section recommendations">
            <h2>💡 Training Recommendations</h2>
            <ul>
              ${recommendations.map(rec => `<li>${rec}</li>`).join('')}
            </ul>
          </div>
          
          <div class="section">
            <h2>✅ Data Quality Assessment</h2>
            <div class="quality-grid">
              <div class="quality-item ${quality.validR2 ? 'pass' : 'fail'}">
                <div class="quality-icon">${quality.validR2 ? '✓' : '✗'}</div>
                <div class="quality-text">Valid R² (≥0.95)</div>
              </div>
              <div class="quality-item ${quality.uniformSpacing ? 'pass' : 'fail'}">
                <div class="quality-icon">${quality.uniformSpacing ? '✓' : '✗'}</div>
                <div class="quality-text">Uniform Load Spacing</div>
              </div>
              <div class="quality-item ${quality.freeSprintPresent ? 'pass' : 'fail'}">
                <div class="quality-icon">${quality.freeSprintPresent ? '✓' : '✗'}</div>
                <div class="quality-text">Free Sprint Present</div>
              </div>
              <div class="quality-item ${quality.sufficientDataPoints ? 'pass' : 'fail'}">
                <div class="quality-icon">${quality.sufficientDataPoints ? '✓' : '✗'}</div>
                <div class="quality-text">Sufficient Data Points (≥4)</div>
              </div>
              <div class="quality-item ${quality.adequateVelocityRange ? 'pass' : 'fail'}">
                <div class="quality-icon">${quality.adequateVelocityRange ? '✓' : '✗'}</div>
                <div class="quality-text">Adequate Velocity Range (≥50%)</div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="footer">
          <p>Generated by T-Apex Performance Analysis System</p>
          <p>Advanced Sprint Analytics • Force-Velocity Profiling • Training Optimization</p>
        </div>
      </div>
    </body>
    </html>
  `;

  // Create and download HTML file that can be printed as PDF
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `T-Apex-Report-${athlete.name}-${session.date}.html`;
  a.click();
  URL.revokeObjectURL(url);
  
  // Also open in new window for printing
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }
}